/**
 * Subscription identifiers for plan matrix highlighting.
 * Resolution order matches manage-subscription: Stripe price id → plan id → name.
 */

export const normalizePlanLabel = (value) => String(value || '').trim().toLowerCase();

/** True when a plan is sold as lifetime / one-time (not recurring subscription). */
export const isLifetimePlanRecord = (plan) => {
	if (!plan) return false;
	if (plan.isLifetimeAccessPlan === true) return true;
	const hasOneTime = Boolean(plan.stripePriceIdOneTime);
	const hasRecurring = Boolean(plan.stripePriceIdMonthly || plan.stripePriceIdYearly);
	return hasOneTime && !hasRecurring;
};

/** True when the company has no subscription / lifetime plan record yet. */
export const hasNeverHadSubscriptionPlan = (subscription) => {
	if (!subscription) return true;
	return !(
		subscription.id ||
		subscription.mongoId ||
		subscription.planId ||
		subscription.planName ||
		subscription.paymentSubscriptionIntentId ||
		subscription.subscriptionPriceId
	);
};

const planStripePriceIds = (plan) =>
	[
		plan?.stripePriceId,
		plan?.stripePriceIdMonthly,
		plan?.stripePriceIdYearly,
		plan?.stripePriceIdOneTime,
	]
		.filter(Boolean)
		.map((id) => String(id));

/**
 * Resolve exactly one current plan column index (or -1).
 * Aligns with SubscriptionPlanDowngradeUpgrade: billing-cycle price match first.
 */
export const resolveCurrentPlanIndex = (plans, identifiers = {}, isYearly = false) => {
	const list = Array.isArray(plans) ? plans : [];
	const { subscriptionPriceId, planId, planName } = identifiers;

	if (subscriptionPriceId) {
		const ref = String(subscriptionPriceId);

		const cycleIdx = list.findIndex((plan) => {
			const cyclePriceId = isYearly
				? plan.stripePriceIdYearly
				: plan.stripePriceIdMonthly;
			return cyclePriceId && String(cyclePriceId) === ref;
		});
		if (cycleIdx >= 0) return cycleIdx;

		const anyPriceIdx = list.findIndex((plan) => planStripePriceIds(plan).includes(ref));
		if (anyPriceIdx >= 0) return anyPriceIdx;
	}

	if (planId) {
		const ref = String(planId);
		const idx = list.findIndex((plan) => {
			const mongoId = String(plan._id || plan.id || '');
			return mongoId && mongoId === ref;
		});
		if (idx >= 0) return idx;
	}

	if (planName) {
		const normalized = normalizePlanLabel(planName);
		const idx = list.findIndex((plan) => {
			const label = normalizePlanLabel(plan.planName || plan.name || '');
			return label && label === normalized;
		});
		if (idx >= 0) return idx;
	}

	return -1;
};

/** Match the catalog plan row for an active subscription (price id first, then plan id, then name). */
export const matchPlanFromSubscription = (plans, subscription) => {
	if (!Array.isArray(plans) || !plans.length || !subscription) return null;

	const billingCycle = String(subscription.billingCycle || '').toLowerCase();
	const isYearly = ['yearly', 'year', 'annual'].includes(billingCycle);
	const idx = resolveCurrentPlanIndex(
		plans,
		{
			subscriptionPriceId: subscription.subscriptionPriceId,
			planId: subscription.planId,
			planName: subscription.planName,
		},
		isYearly,
	);

	return idx >= 0 ? plans[idx] : null;
};

export const resolveSubscriptionPlanMatrixContext = (
	subscription,
	{ currentPlanName: fallbackPlanName } = {},
) => {
	if (!subscription) {
		return {
			subscriptionPriceId: null,
			planId: null,
			currentPlanId: null,
			currentPlanName: fallbackPlanName || null,
			currentPlanPrice: null,
			subscriptionStatus: null,
			isYearlyBilling: false,
			isLifetime: false,
			hasActiveSubscription: false,
			hasNeverHadPlan: true,
		};
	}

	const billingCycle = String(subscription.billingCycle || '').toLowerCase();
	const isYearlyBilling = ['yearly', 'year', 'annual'].includes(billingCycle);
	const isLifetime = billingCycle === 'lifetime';

	return {
		subscriptionPriceId: subscription.subscriptionPriceId || null,
		planId: subscription.planId || null,
		currentPlanId: subscription.subscriptionPriceId || null,
		currentPlanName: subscription.planName || fallbackPlanName || null,
		currentPlanPrice: subscription.amount ?? null,
		subscriptionStatus: subscription.status || null,
		isYearlyBilling,
		isLifetime,
		hasActiveSubscription: Boolean(subscription.hasActiveSubscription),
		hasNeverHadPlan: hasNeverHadSubscriptionPlan(subscription),
	};
};

export default resolveSubscriptionPlanMatrixContext;
